import React, { useState, Fragment } from 'react';
import '../scss/components/DataTables.scss';
import { Form, Container, Col, Row, Button } from 'react-bootstrap';
import { useHistory } from 'react-router-dom';
import AsyncSelect from 'react-select/async';
import DataTable from '../components/DataTable';
import { useSelector, useDispatch } from 'react-redux';
import { AuthHandler } from '../utils/AuthHandler';
import { RestService } from '../utils';
import ConfigHandler from '../config';
import Utils from '../utils/Utils';
import { useTranslation } from 'react-i18next';

function FeasyList() {
  let userRole = false;
  const { t } = useTranslation();

  if (AuthHandler.isAuth()) {
    userRole = AuthHandler.getUserData();
  }

  const state = useSelector((state) => state);

  //Needed Variables
  const [listData, setData] = useState([]);
  const [pageCount, setPageCount] = useState(0);
  const [sortDirection, setSortDir] = useState(false);
  const [sortVal, setSortVal] = useState('dateInserted:desc');
  const [numofData, setnumofData] = useState(0);
  const [sortIcon, setSortIcon] = useState('expairingDate');
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [toCloneData, setToCloneData] = useState(null);
  const [selectedOption, setSelectedOption] = useState(null);

  let dataReturn = [];

  //Search Functionalities
  const [inputText, setInputText] = useState('');
  const dispatch = useDispatch();

  //Fetch new table data when needed
  const fetchData = React.useCallback(({ pageSize, pageIndex }) => {
    const startRow = pageSize * pageIndex;
    const endRow = startRow + pageSize;
    dataReturn = RestService.get({
      url: `${ConfigHandler.getConfig().api.baseUrl}${ConfigHandler.getConfig().api.path.getFeasibilityList
        }?q=${inputText}&offset=${startRow}&limit=${pageSize}&order=${sortVal}`
    }).then((response) =>
      response
        .json()
        .then((data) => ({
          data: data
        }))
        .then((res) => {
          dataReturn = res.data.data;
          if (dataReturn != undefined) {
            setData(dataReturn);
          } else if (dataReturn === undefined) {
            document.getElementsByClassName(`TH-${sortIcon}`)[0].classList.remove('sortedTabAsc', 'sortedTabDesc');
          }
          setnumofData(res.data.total);
          setPageCount(Math.ceil(res.data.total / pageSize));
        })
    );
    const userData = AuthHandler.getUserData();
  });

  //Get user search data
  let inputHandler = (e) => {
    let lowerCase = e.target.value.toLowerCase();
    Utils.handleInputSearch(lowerCase).then((text) => {
      setInputText(text);
    });
  };

  //Handle sorting
  const sortHandler = (val) => {
    document.getElementsByClassName(`TH-${val}`)[0].classList.remove('sortedTabAsc', 'sortedTabDesc');
    if (sortDirection) {
      setSortVal(`${val}:asc`);
      setSortDir(false);
      document.getElementsByClassName(`TH-${val}`)[0].classList.add('sortedTabAsc');
      setSortIcon(val);
    } else {
      setSortVal(`${val}:desc`);
      setSortDir(true);
      document.getElementsByClassName(`TH-${val}`)[0].classList.add('sortedTabDesc');
      setSortIcon(val);
    }
  };

  //Columns
  let columns = React.useMemo(
    () => [
      {
        customWidth: '100px',
        Header: '#',
        sortable: false,
        accessor: 'id',
        Cell: ({ row }) => {
          const { id, submittedDate } = row.original;
          const dt = submittedDate ? submittedDate.toString().replace(/-/g, '').slice(2, 8) : '';
          const dt_id = dt ? `${dt}.${id}` : "";
          return (
            <span>{dt_id}</span>
          );
        }
      },
      {
        customWidth: '90px',
        Header: t('FORMS.CreationDateLabel'),
        accessor: 'submittedDate',
        Cell: ({ value }) => {
          const dt = value ? (new Date(value)).toLocaleDateString() : '';
          return (
            <span>{dt}</span>
          )
        }
      },
      {
        customWidth: '130px',
        Header: t('FORMS.ReferenceNameLabel'),
        sortable: false,
        accessor: 'reference',
        Cell: ({ row }) => {
          const { referenceName, referencePhone } = row.original;
          const partnerName = row.original.partner?.name;
          if ((userRole) && (userRole.role[0].toLowerCase() === 'admin')) {
            return (
              <span span >
                <strong><u>{partnerName}</u></strong><br />
                <strong>{referenceName}</strong><br />
                <small>{referencePhone}</small>
              </span >
            );
          } else {
            return (
              <span>
                <strong>{referenceName}</strong><br />
                <small>{referencePhone}</small>
              </span >
            );
          }
        }
      },
      {
        customWidth: '100px',
        Header: t('FORMS.BandwidthLabel'),
        sortable: false,
        accessor: 'bandwidth',
        Cell: ({ row }) => {
          const { feasyType, bandwidth, voipType, voipNumLines, colocationPop, colocationRu, colocationPower } = row.original;
          return (
            <>
              {feasyType === 'fiber' && (
                <small>
                  <b><i>FIBRA DEDICATA</i></b><br />
                  <span>{bandwidth}</span>
                </small>
              )}
              {feasyType === 'voip' && (
                <small>
                  <b><i>VOIP</i></b><br />
                  <span>{voipType}<br />{voipNumLines}</span>
                </small>
              )}
              {feasyType === 'colocation' && (
                <small>
                  <b><i>COLOCATION</i>></b><br />
                  <span>{colocationRu}<br />{colocationPop}<br />({colocationPower})</span>
                </small>
              )}
            </>
          );
        }
      },
      {
        customWidth: '135px',
        Header: t('FORMS.CoordinatesLabel'),
        sortable: false,
        accessor: 'coordinates',
        Cell: ({ row }) => {
          const { latitude, longitude } = row.original;
          return (
            <>
              {(latitude || longitude) && (
                <small>
                  <span>
                    <b>Lat.</b> {latitude.toFixed(8)}<br />
                    <b>Long.</b> {longitude.toFixed(8)}
                  </span >
                </small>
              )
              }
            </>
          );
        }
      },
      {
        Header: t('FORMS.LocationLabel'),
        sortable: false,
        accessor: 'location',
        Cell: ({ row }) => {
          const { city, street, streetNumber, buildingNumber, istatCode, egonCode, consegnaPop } = row.original;
          return (
            <span>
              {city ? (
                <>
                  <small><i>({istatCode} | {egonCode})</i></small><br />
                  <strong>{street}, {streetNumber} {buildingNumber}<br />
                    {city}</strong>
                </>
              ) : ""}
              {consegnaPop && (
                <>
                  <br /> <br />
                  <small><strong>L2 Pop:</strong> {consegnaPop}</small>
                </>
              )
              }
            </span >
          );
        }
      },
      {
        Header: t('FORMS.CommentsLabel'),
        accessor: 'note',
        Cell: ({ value }) => {
          return (
            <small>{value}</small>
          );
        }
      }
    ],
    []
  );

  if (AuthHandler.isAuth()) {
    const userRole = AuthHandler.getUserData();
  }

  if (listData == '' || listData === undefined) setData([{}]);

  const history = useHistory();
  const options = [
    { value: 'fiber', label: 'Fibra Dedicata' },
    { value: 'voip', label: 'VOIP' },
    { value: 'colocation', label: 'Colocation' },
  ];

  const handleButton = () => {
    if (selectedOption) {
      history.push(`/requests/newFeasibility?q=${selectedOption.value}`);
    }
  };

  return (
    <Container fluid>
      <Row className="quoteFeasy">
        {
          (userRole?.role[0].toLowerCase() === 'broker') && (
            <>
              <h2 className="fadedText">{t('Inserisci Nuova Richiesta')}</h2>
              <Row className="mb-5 align-items-center">
                <Col xs="4">&nbsp;
                  <AsyncSelect
                    cacheOptions
                    defaultOptions={options}
                    options={options}
                    isClearable={true}
                    value={selectedOption}
                    onChange={setSelectedOption}
                    placeholder={'Fai la tua scelta'}
                  />
                </Col>
                {
                  <Col xs="auto">
                    <Button disabled={!selectedOption} onClick={handleButton} >{t('QUOTELIST.AddRequest')}</Button>
                  </Col>
                }
              </Row>
            </>
          )
        }
      </Row>
      <Row className="quoteFeasy">
        {listData.length && (
          <DataTable
            columns={columns}
            data={listData}
            heading={true}
            searchBar={true}
            numofData={numofData}
            tableHeading={t('QUOTELIST.FEASIBILITY.TableHeading')}
            searchMessage={t('OTHERS.Search')}
            filterButton={false}
            downloadButton={false}
            inputHandler={inputHandler}
            fetchData={fetchData}
            pageCount={pageCount}
            sortHandler={sortHandler}
            sortValue={sortVal}
            inputText={inputText}
            paginationItemsStartingPoint={ConfigHandler.getConfig().datatables.defaultStartingPoint}
            paginationItemsCount={ConfigHandler.getConfig().datatables.defaultPaginationCount}
          />
        )}
      </Row>
    </Container >
  );
}

export default FeasyList;
